import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { queryCodeDirectories } from '@/features/codeCoverage/data/codeDirectories';
import { queryCoverageFiles } from '@/features/codeCoverage/data/coverageFiles';
import { queryCoverageRuns } from '@/features/codeCoverage/data/coverageRuns';
import { queryExecutionRuns } from '@/features/executionRuns/data/executionRuns';
import { querySuiteRuns, queryTestRuns } from '@/features/executions/data';
import { queryFeatures } from '@/features/features/data/features';
import { queryJobRuns, queryJobs } from '@/features/jobs/data/jobs';
import {
  queryPipelineRunSummary,
  queryPipelines,
  queryPipelineStageExecutions,
  queryPipelineStages,
} from '@/features/pipelines/data';
import { queryReleases } from '@/features/releases/data/releases';
import { querySuites } from '@/features/suites/data/suites';
import { queryTests } from '@/features/tests/data/tests';
import { queryResultsFiles, queryUploads } from '@/features/uploads/data';
import { queryKeys } from '@/shared/hooks';
import { r, s } from '@/theme/tokens';
import { Box, InfoField, Panel, Stack, Text } from '@/ui';
import { buildEqFilter } from '@/ui/composites/grid/gridFilters';
import {
  settingsCopyTextSx,
  settingsPanelSurfaceSx,
} from '@/ui/composites/settingsPageTypographySx';
import { CopyIcon, IconButton } from '@/ui/primitives';
import type { ProjectDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type ProjectOverviewData = {
  releasesTotal: number;
  featuresTotal: number;
  uploadsTotal: number;
  resultsFilesTotal: number;
  executionRunsTotal: number;
  suitesTotal: number;
  suiteRunsTotal: number;
  testsTotal: number;
  testRunsTotal: number;
  coverageRunsTotal: number;
  codeDirectoriesTotal: number;
  coverageFilesTotal: number;
  pipelinesTotal: number;
  pipelineRunsTotal: number;
  stagesTotal: number;
  stageRunsTotal: number;
  jobsTotal: number;
  jobRunsTotal: number;
};

type ProjectModuleKey = 'delivery' | 'code' | 'test' | 'operations';

const overviewRowSx = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: s('sm'),
} as const;

const overviewPairLabelSx = {
  display: 'flex',
  alignItems: 'baseline',
  gap: s('sm'),
  flexWrap: 'wrap',
} as const;

const overviewPairDividerSx = {
  ...settingsCopyTextSx,
  color: 'textSecondary',
} as const;

const settingsRailPanelSx = {
  width: '100%',
  py: s('md'),
} as const;

const overviewLabelTextSx = {
  ...settingsCopyTextSx,
} as const;

const ProjectOverviewRow = ({
  label,
  value,
}: {
  label: ReactElement | string;
  value: string;
}): ReactElement => (
  <Box sx={overviewRowSx}>
    {typeof label === 'string' ? (
      <Text role="body" color="textSecondary" sx={overviewLabelTextSx}>
        {label}
      </Text>
    ) : (
      label
    )}
    <Text role="body">{value}</Text>
  </Box>
);

const ProjectOverviewPairLabel = ({ primary }: { primary: string }): ReactElement => (
  <Box sx={overviewPairLabelSx}>
    <Text role="body" color="textSecondary" sx={overviewLabelTextSx}>
      {primary}
    </Text>
    <Text role="body" color="textSecondary" sx={overviewPairDividerSx}>
      /
    </Text>
    <Text role="body" color="textSecondary" sx={overviewLabelTextSx}>
      Runs
    </Text>
  </Box>
);

const ProjectModulePanel = ({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ key: string; label: ReactElement | string; value: string }>;
}): ReactElement => (
  <Panel surfaceVariant="settings" sx={[settingsPanelSurfaceSx, settingsRailPanelSx]}>
    <Stack gap="sm">
      <Text role="body">{title}</Text>
      {rows.map((row) => (
        <ProjectOverviewRow key={row.key} label={row.label} value={row.value} />
      ))}
    </Stack>
  </Panel>
);

const useProjectOverviewData = ({
  projectId,
  organisationId,
}: {
  projectId: string | null;
  organisationId: string | null;
}): {
  projectId: string | null;
  overviewData: ProjectOverviewData | undefined;
  resolveCount: (value: number | null | undefined) => string;
} => {
  const projectIds = useMemo(() => (projectId ? [projectId] : []), [projectId]);
  const projectIdsKey = useMemo(() => JSON.stringify(projectIds), [projectIds]);
  const projectFilter = useMemo(() => buildEqFilter('projectId', projectId), [projectId]);
  const overviewQuery = useQuery({
    queryKey: queryKeys.projectSettingsOverview(organisationId ?? null, projectIdsKey),
    enabled: projectIds.length > 0 && Boolean(projectId),
    queryFn: async () => {
      const emptyCountResult = { total: 0 };
      const emptyPipelineRunSummary = { buildTotal: 0 };
      const [
        releasesResult,
        featuresResult,
        uploadsResult,
        resultsFilesResult,
        executionRunsResult,
        suitesResult,
        suiteRunsResult,
        testsResult,
        testRunsResult,
        coverageRunsResult,
        codeDirectoriesResult,
        coverageFilesResult,
        pipelinesResult,
        pipelineRunsSummary,
        stagesResult,
        stageRunsResult,
        jobsResult,
        jobRunsResult,
      ] = await Promise.all([
        queryReleases(projectId, organisationId, {
          page: 1,
          pageSize: 1,
          sorts: [],
          filters: [],
        }).catch(() => emptyCountResult),
        queryFeatures(projectId, organisationId, {
          page: 1,
          pageSize: 1,
          sorts: [],
          filters: [],
        }).catch(() => emptyCountResult),
        queryUploads({ page: 1, pageSize: 1, filters: projectFilter, summaryMode: 'none' }).catch(
          () => emptyCountResult,
        ),
        queryResultsFiles({
          page: 1,
          pageSize: 1,
          filters: projectFilter,
          includeSummary: false,
        }).catch(() => emptyCountResult),
        queryExecutionRuns(
          {
            page: 1,
            pageSize: 1,
            filters: projectFilter,
            sorts: [],
          },
          organisationId,
        ).catch(() => emptyCountResult),
        querySuites(projectId, organisationId, {
          page: 1,
          pageSize: 1,
          sorts: [],
          filters: [],
        }).catch(() => emptyCountResult),
        querySuiteRuns(projectId, organisationId, {
          page: 1,
          pageSize: 1,
          sorts: [],
          filters: [],
        }).catch(() => emptyCountResult),
        queryTests(projectId, organisationId, {
          page: 1,
          pageSize: 1,
          sorts: [],
          filters: [],
        }).catch(() => emptyCountResult),
        queryTestRuns(projectId, organisationId, {
          page: 1,
          pageSize: 1,
          sorts: [],
          filters: [],
        }).catch(() => emptyCountResult),
        queryCoverageRuns({ page: 1, pageSize: 1, filters: projectFilter }, organisationId).catch(
          () => emptyCountResult,
        ),
        queryCodeDirectories(
          { page: 1, pageSize: 1, filters: projectFilter },
          organisationId,
        ).catch(() => emptyCountResult),
        queryCoverageFiles({ page: 1, pageSize: 1, filters: projectFilter }, organisationId).catch(
          () => emptyCountResult,
        ),
        queryPipelines(projectId, organisationId, {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'createdAt', direction: 'desc' }],
          filters: [],
        }).catch(() => emptyCountResult),
        queryPipelineRunSummary(projectId, organisationId, {
          page: 1,
          pageSize: 1,
          sorts: [],
          filters: [],
        }).catch(() => emptyPipelineRunSummary),
        queryPipelineStages(projectId, organisationId, {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'startedAt', direction: 'desc' }],
          filters: [],
        }).catch(() => emptyCountResult),
        queryPipelineStageExecutions(projectId, organisationId, {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'startedAt', direction: 'desc' }],
          filters: [],
        }).catch(() => emptyCountResult),
        queryJobs(projectId, organisationId, {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'jobName', direction: 'asc' }],
          filters: [],
        }).catch(() => emptyCountResult),
        queryJobRuns(projectId, organisationId, {
          page: 1,
          pageSize: 1,
          sorts: [{ field: 'startedAt', direction: 'desc' }],
          filters: [],
        }).catch(() => emptyCountResult),
      ]);

      return {
        releasesTotal: releasesResult.total,
        featuresTotal: featuresResult.total,
        uploadsTotal: uploadsResult.total,
        resultsFilesTotal: resultsFilesResult.total,
        executionRunsTotal: executionRunsResult.total,
        suitesTotal: suitesResult.total,
        suiteRunsTotal: suiteRunsResult.total,
        testsTotal: testsResult.total,
        testRunsTotal: testRunsResult.total,
        coverageRunsTotal: coverageRunsResult.total,
        codeDirectoriesTotal: codeDirectoriesResult.total,
        coverageFilesTotal: coverageFilesResult.total,
        pipelinesTotal: pipelinesResult.total,
        pipelineRunsTotal: pipelineRunsSummary.buildTotal,
        stagesTotal: stagesResult.total,
        stageRunsTotal: stageRunsResult.total,
        jobsTotal: jobsResult.total,
        jobRunsTotal: jobRunsResult.total,
      };
    },
  });

  const resolveCount = (value: number | null | undefined): string => {
    if (overviewQuery.isLoading && value === undefined) {
      return '-';
    }

    return (value ?? 0).toLocaleString('en-GB');
  };

  return {
    projectId,
    overviewData: overviewQuery.data,
    resolveCount,
  };
};

export const ProjectSettingsDetailsRail = ({
  project,
  onCopyProjectId,
}: {
  project: ProjectDto | null;
  onCopyProjectId: () => void;
}): ReactElement | null => {
  const createdDate = new Date(project?.createdAt ?? '');
  const createdLabel = Number.isNaN(createdDate.valueOf())
    ? '-'
    : createdDate.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });

  if (!project) {
    return null;
  }

  return (
    <Panel surfaceVariant="settings" sx={[settingsPanelSurfaceSx, settingsRailPanelSx]}>
      <Stack gap="sm">
        <InfoField
          label="Project ID"
          gap="xxs"
          labelTextRole="body"
          value={
            <Box
              sx={{
                position: 'relative',
                pr: s('3xl'),
              }}
            >
              <Text
                role="body"
                component="span"
                sx={{
                  display: 'block',
                  fontFamily: (theme) => theme.typography.mono.fontFamily,
                }}
              >
                {project.id}
              </Text>
              <IconButton
                size="small"
                aria-label="Copy project ID"
                onClick={onCopyProjectId}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  right: `-${s('md')}`,
                  transform: 'translateY(-50%)',
                  borderRadius: r('pill'),
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <CopyIcon />
              </IconButton>
            </Box>
          }
          labelSx={settingsCopyTextSx}
        />
        <InfoField
          label="Created"
          gap="xxs"
          labelTextRole="body"
          value={createdLabel}
          labelSx={settingsCopyTextSx}
        />
      </Stack>
    </Panel>
  );
};

export const ProjectModulePanelsComparison = ({
  projectId,
  organisationId,
}: {
  projectId: string | null;
  organisationId: string | null;
}): ReactElement | null => {
  return (
    <ProjectModuleOverviewPanels
      projectId={projectId}
      organisationId={organisationId}
      modules={['delivery', 'code', 'test', 'operations']}
    />
  );
};

export const ProjectModuleOverviewPanels = ({
  projectId,
  organisationId,
  modules,
}: {
  projectId: string | null;
  organisationId: string | null;
  modules: ProjectModuleKey[];
}): ReactElement | null => {
  const {
    projectId: resolvedProjectId,
    overviewData,
    resolveCount,
  } = useProjectOverviewData({
    projectId,
    organisationId,
  });

  if (!resolvedProjectId) {
    return null;
  }

  const modulePanels: Record<ProjectModuleKey, ReactElement> = {
    delivery: (
      <ProjectModulePanel
        title="Delivery Module Data"
        rows={[
          { key: 'releases', label: 'Releases', value: resolveCount(overviewData?.releasesTotal) },
          { key: 'features', label: 'Features', value: resolveCount(overviewData?.featuresTotal) },
        ]}
      />
    ),
    code: (
      <ProjectModulePanel
        title="Code Module Data"
        rows={[
          {
            key: 'coverage-runs',
            label: 'Coverage Runs',
            value: resolveCount(overviewData?.coverageRunsTotal),
          },
          {
            key: 'directories-runs',
            label: <ProjectOverviewPairLabel primary="Directories" />,
            value: `${resolveCount(overviewData?.codeDirectoriesTotal)} / ${resolveCount(overviewData?.coverageRunsTotal)}`,
          },
          {
            key: 'files-runs',
            label: <ProjectOverviewPairLabel primary="Files" />,
            value: `${resolveCount(overviewData?.coverageFilesTotal)} / ${resolveCount(overviewData?.coverageRunsTotal)}`,
          },
        ]}
      />
    ),
    test: (
      <ProjectModulePanel
        title="Test Module Data"
        rows={[
          { key: 'uploads', label: 'Uploads', value: resolveCount(overviewData?.uploadsTotal) },
          {
            key: 'results-files',
            label: 'Results Files',
            value: resolveCount(overviewData?.resultsFilesTotal),
          },
          {
            key: 'execution-runs',
            label: 'Execution Runs',
            value: resolveCount(overviewData?.executionRunsTotal),
          },
          {
            key: 'suites-runs',
            label: <ProjectOverviewPairLabel primary="Suites" />,
            value: `${resolveCount(overviewData?.suitesTotal)} / ${resolveCount(overviewData?.suiteRunsTotal)}`,
          },
          {
            key: 'tests-runs',
            label: <ProjectOverviewPairLabel primary="Tests" />,
            value: `${resolveCount(overviewData?.testsTotal)} / ${resolveCount(overviewData?.testRunsTotal)}`,
          },
        ]}
      />
    ),
    operations: (
      <ProjectModulePanel
        title="Operations"
        rows={[
          {
            key: 'pipelines-runs',
            label: <ProjectOverviewPairLabel primary="Pipelines" />,
            value: `${resolveCount(overviewData?.pipelinesTotal)} / ${resolveCount(overviewData?.pipelineRunsTotal)}`,
          },
          {
            key: 'stages-runs',
            label: <ProjectOverviewPairLabel primary="Stages" />,
            value: `${resolveCount(overviewData?.stagesTotal)} / ${resolveCount(overviewData?.stageRunsTotal)}`,
          },
          {
            key: 'jobs-runs',
            label: <ProjectOverviewPairLabel primary="Jobs" />,
            value: `${resolveCount(overviewData?.jobsTotal)} / ${resolveCount(overviewData?.jobRunsTotal)}`,
          },
        ]}
      />
    ),
  };

  return (
    <Stack gap="lg">
      {modules.map((module) => (
        <Box key={module}>{modulePanels[module]}</Box>
      ))}
    </Stack>
  );
};
